import * as React from 'react';
import { observer } from 'mobx-react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { ResultKind } from 'services/base/types';
import { ADD_PANGOLIN_PAGE_TYPES, HistoryService } from 'services/base/history';
import { BasePath } from 'services/base/page_path';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { Link } from 'ui/base/link';
import { BackButton } from 'ui/base/button/back_button';
import { ActiveButton, DefaultButton } from 'ui/public/button';
import { PartitionedPage } from 'ui/add_pangolin/partitioned_page/partitioned_page';
import styles from './add_pangolin_method.module.css';
import Messages from './add_pangolin_method_messages';
import { AddPangolinMethodStore } from './add_pangolin_method_store';
import { PLATFORM } from 'services/base/config';
import { Redirect } from 'pages/core/redirect/redirect';

type BaseAddPangolinMethodProps = {
  store: AddPangolinMethodStore;
};

export const BaseAddPangolinMethod = observer(({ store }: BaseAddPangolinMethodProps) => {
  const accounts = useAccountsDomain();
  const history = useHistory();
  const handleQRCode = React.useCallback(async() => {
    const result = await store.busy.perform(() => store.handleQRCode(accounts));
    /* eslint-disable no-alert */
    if (result == null) {
      // QR Code Scanning was cancelled. Do nothing.
    } else if (result.kind === ResultKind.OK) {
      // Clear all the pangolin pages
      const cleared = HistoryService.clearWhile(history, ADD_PANGOLIN_PAGE_TYPES);
      if (!cleared) {
        // If any of them remain on the stack, it means they were the only things there
        // so we must replace the last remaining one with the Home page
        await HistoryService.push(history, { kind: BasePath.HOME, pangolinID: result.pangolin.id });
        await HistoryService.push(history, { kind: BasePath.ADD_PANGOLIN_RENAME, pangolinID: result.pangolin.id });
      } else {
        await HistoryService.push(history, { kind: BasePath.ADD_PANGOLIN_RENAME, pangolinID: result.pangolin.id });
      }
    } else if (result.kind === ResultKind.USER_ERROR) {
      alert(Messages.invalidQRCodeError());
    } else {
      alert(Messages.systemError());
    }
    /* eslint-enable no-alert */
  }, [accounts, history, store]);

  const top = (
    <>
      <BackButton/>
      <div className={styles.title}>
        {Messages.title()}
      </div>
    </>
  );
  const middle = <div className={styles.image}/>;
  const bottom = (
    <>
      <div className={styles.action}>
        <ActiveButton busy={store.busy} onClick={handleQRCode}>
          {Messages.qrCode()}
        </ActiveButton>
      </div>
      <div className={styles.action}>
        <Link path={{ kind: BasePath.ADD_PANGOLIN_MNEMONIC }}>
          <DefaultButton>
            {Messages.secretPhrase()}
          </DefaultButton>
        </Link>
      </div>
    </>
  );

  return (
    <PartitionedPage top={top} middle={middle} bottom={bottom}/>
  );
});

type AddPangolinMethodProps = RouteComponentProps;
type AddPangolinMethodState = {
  store: AddPangolinMethodStore;
};
export class AddPangolinMethod extends React.Component<AddPangolinMethodProps, AddPangolinMethodState> {
  constructor(props: RouteComponentProps) {
    super(props);
    this.state = {
      store: new AddPangolinMethodStore(),
    };
  }
  render() {
    if (PLATFORM === 'web') return <Redirect />;
    return <BaseAddPangolinMethod store={this.state.store}/>;
  }
}
